/**
 * @fileoverview Input/output schemas for image-classifier-v1.
 *
 * This agent classifies photos of people into framing categories:
 * portrait, half-body, or full-body. It can also detect invalid images.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';

// =============================================================================
// IMAGE MODE ENUM
// =============================================================================

/**
 * The possible image framing modes.
 */
export const ImageModeSchema = z.enum(['portrait', 'half-body', 'full-body']);
export type ImageMode = z.infer<typeof ImageModeSchema>;

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 * Requires exactly one image to classify.
 */
export const InputSchema = BaseFlowInputSchema.extend({
  /**
   * Image data URI (base64 encoded) to classify.
   * Must be in format: data:image/jpeg;base64,... or data:image/png;base64,...
   */
  imageDataUri: z.string().describe(
    'Image data URI (base64 encoded) to classify. Must contain a photo of a single person.'
  ),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// HOOK INPUT SCHEMA
// =============================================================================

/**
 * Simplified input from UI hooks.
 */
export const HookInputSchema = z.object({
  /**
   * Image data URI (base64 encoded) to classify.
   */
  imageDataUri: z.string().describe('Image data URI to classify.'),
});

export type HookInput = z.infer<typeof HookInputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output schema for the image classification result.
 */
export const OutputSchema = z.object({
  /**
   * Whether the image is valid (contains exactly one person).
   */
  isValid: z.boolean().describe(
    'True if the image contains exactly one person and can be classified. False otherwise.'
  ),
  /**
   * The detected image mode. Only present if isValid is true.
   */
  mode: ImageModeSchema.optional().describe(
    'The detected framing mode: portrait, half-body, or full-body. Only present if isValid is true.'
  ),
  /**
   * Error message if the image is invalid.
   */
  error: z.string().optional().describe(
    'Explanation of why the image is invalid, if isValid is false.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
